<template>
	<article>
    <header class="view__header">
      <h2 class="view__title">{{ L.localize("Label_Invoice") }}</h2>
    </header>
    <article class="view__content">
      <div class="view__panel">
        <submenu />

        <Btn @click="triggerSendInvoice" label="Label_SendByEmail" float="right" :withBorder="true" />

        <sweet-modal :title="L.localize('Label_SendByEmail')" ref="sendInvoiceModel">
          <p>{{ L.localize("Label_SendByEmail_Message") }}</p>
          <Btn v-on:click="sendInvoice(invoice.id)" label="Account_PasswordReset_Form_Submit" />
        </sweet-modal>

        <div class="invoice">
          <table class="invoice__main" cellpadding="0" cellspacing="0">
              <tr class="top">
                  <td colspan="4">
                      <table>
                          <tr>
                              <td class="title">{{ currentTenant.name }}</td>
                              <td>
                                  <span class="invoice__id">
                                    {{ Format.id(invoice.id) }}
                                  </span><br>
                                  <span class="invoice__creationDate">
                                    {{ L.localize("Label_CreationDate") }}: {{ Format.date(invoice.creationTime) }}
                                  </span><br>
                                  <span v-if="invoice.paidTime">
                                    {{ L.localize("Label_PaidTime") }}: {{ Format.date(invoice.paidTime) }}
                                  </span>
                              </td>
                          </tr>
                      </table>
                  </td>
              </tr>

              <tr class="heading">
                  <td>{{ L.localize("Label_Item") }}</td>
                  <td>{{ L.localize("Label_Price") }}</td>
                  <td>{{ L.localize("Label_Quantity") }}</td>
                  <td>{{ L.localize("Label_Total") }}</td>
              </tr>

              <tr class="item item--highlight">
                  <td>{{ invoice.description }}</td>
                  <td>{{ Format.money(invoice.amount) }}</td>
                  <td>1</td>
                  <td>{{ Format.money(invoice.amount) }}</td>
              </tr>
              <template v-if="invoice.taxes.length > 0" v-for="(tax, index) in invoice.taxes">
                  <tr class="item item--highlight" :class="index == invoice.taxes.length - 1 ? 'last' : ''">
                      <td>{{ tax.name }}</td>
                      <td>{{ Format.percent(tax.amount) }}</td>
                      <td>1</td>
                      <td>{{ Format.money(tax.total) }}</td>
                  </tr>
              </template>

              <tr class="total">
                  <td></td>
                  <td></td>
                  <td></td>
                  <td>Total: {{ Format.money(invoice.total) }}</td>
              </tr>
          </table>
        </div>
        <div class="invoice">
          <table class="invoice__balance" cellpadding="0" cellspacing="0">
            <tr class="heading">
              <td>{{ L.localize("Label_Balance") }}</td>
              <td></td>
              <td></td>
              <td></td>
            </tr>

            <template v-for="(charge, index) in invoice.charges">
              <tr class="item item--highlight">
                <td>{{ Format.date(charge.chargeTime) }}</td>
                <td></td>
                <td></td>
                <td>- {{ Format.money(charge.amount) }}</td>
              </tr>
            </template>
            <tr class="item last item--highlight">
              <td>{{ L.localize("Label_BalanceDue") }}</td>
              <td></td>
              <td></td>
              <td>{{ Format.money(dueBalance) }}</td>
            </tr>
          </table>
        </div>
        <h3 class="view__title--sub">{{ L.localize("Label_Charges") }}</h3>
        <div class="invoice">
          <table class="invoice__charges" cellpadding="0" cellspacing="0">
            <tr class="heading">
                <td>{{ L.localize("Label_Amount") }}</td>
                <td>{{ L.localize("Label_ChargeTime") }}</td>
                <td>{{ L.localize("Label_ChargeType") }}</td>
                <td>{{ L.localize("Label_Reference") }}</td>
                <td>{{ L.localize("Label_Note") }}</td>
              </tr>

            <template v-for="(charge, index) in charges">
              <tr class="item last item--highlight" v-if="charge.editMode">
                <td><TextBox v-model="charge.amount" type="number" :min="0" :max="10000" step="0.01" acc="amount" /></td>
                <td><TextBox v-model="charge.chargeTime" type="date" acc="reference" /></td>
                <td><SelectList v-model="charge.chargeType" :options="chargeTypes" acc="chargeType" /></td>
                <td><TextBox v-model="charge.reference" acc="reference" /></td>
                <td><TextBox v-model="charge.note" :multiLine="true" acc="note" /></td>
              </tr>
              <tr class="item" v-if="charge.editMode">
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td>
                  <Btn label="Label_SaveButton" @click="saveCharge(charge, index)" :acc="acc.generate('save-charge')" />
                  <Btn label="Label_Delete" @click="removeCharge(invoice, charge, index)" :acc="acc.generate('delete-charge')" />
                </td>
              </tr>
              <tr class="item last item--highlight" v-if="!charge.editMode">
                <td>{{ Format.money(charge.amount) }}</td>
                <td>{{ Format.date(charge.chargeTime) }}</td>
                <td>{{ charge.chargeType }}</td>
                <td>{{ charge.reference }}</td>
                <td>{{ charge.note }}</td>
              </tr>
              <tr class="item" v-if="!charge.editMode">
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td>
                  <Btn label="Label_Edit" @click="editCharge(charge, index)" :acc="acc.generate('edit-charge')" />
                  <Btn label="Label_Delete" @click="removeCharge(invoice, charge, index)" :acc="acc.generate('delete-charge')" />
                </td>
              </tr>
            </template>
          </table>
          <Btn label="Label_Create" @click="createCharge" :acc="acc.generate('create-charge')" />
        </div>
      </div>
    </article>
  </article>
</template>

<script>
// https://github.com/NextStepWebs/simple-html-invoice-template
import Format from 'services/Format'
import { mapGetters } from 'vuex'
import L from 'services/Localization'
import { Btn, TextBox, SelectList } from 'components/layout/forms'
import moment from 'moment'
import _ from 'lodash'
import Submenu from 'components/views/members/Submenu'
import acc from 'services/Acceptance'
import { SweetModal } from 'sweet-modal-vue'

export default {
  name: 'invoice',
  components: { Submenu, Btn, TextBox, SelectList, SweetModal },
  data() {
    return {
      L: L,
      acc: acc,
      Format: Format,
      charges: [],
      chargeTypes: ['Stripe', 'Bank', 'Other']
    }
  },
  created() {
    this.$store.dispatch('getMember', this.$route.params.id)
    this.getInvoice()
    this.$store.dispatch('getTenantSession')
  },
  computed: {
    ...mapGetters({
      member: 'activeMember',
      languages: 'getLanguages',
      invoice: 'activeInvoice',
      currentTenant: 'getCurrentTenant'
    }),
    dueBalance: function () {
      var total = 0;
      if (!this.invoice.charges) {
        return total
      }
      this.invoice.charges.forEach(function (charge, index) {
        total += charge.amount;
      }, this);

      if (total > this.invoice.total) {
        return 0;
      }

      return this.invoice.total - total;
    }
  },
  methods: {
    triggerSendInvoice: function() {
      this.$refs.sendInvoiceModel.open()
    },
    sendInvoice: function(invoiceId) {
      window.abp.services.app.invoice.sendEmail({invoiceId, "lang": this.languages.current}).then(() => {
        this.$refs.sendInvoiceModel.close()
      })
    },
    getInvoice: function() {
      this.$store.dispatch('getInvoice', this.$route.params.invoiceId).then(() => {
        this.charges = _.cloneDeep(this.invoice.charges)
      })
    },
    createCharge: function () {
      this.charges.push({
        amount: this.invoice.total,
        reference: '',
        chargeTime: moment().format('MM/DD/YYYY'),
        note: '',
        chargeType: 'Bank',
        editMode: true
      })
    },
    saveCharge: function (charge, index) {
      charge.editMode = false;
      let request = _.cloneDeep(charge)
      request.invoiceId = this.invoice.id
      this.$store.dispatch('saveCharge', request).then(() => {
        this.getInvoice()
      })
    },
    editCharge: function (charge, index) {
      this.$set(this.charges[index], 'editMode', true)
    },
    removeCharge: function (invoice, charge, index) {
      this.charges.splice(index, 1);
      if (charge.Id !== typeof (undefined)) {
        let request = { invoiceId: invoice.id, id: charge.id }
        this.$store.dispatch('removeCharge', request).then(() => {
          this.getInvoice()
        })
      }
    }
  }
}
</script>

<style lang='scss'>

</style>



// WEBPACK FOOTER //
// Invoice.vue?7d20b83b